import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { api, type GeoSettings } from "../api";

// GeoSettingsContext exposes the current geo feature flags (ixp_enabled,
// nix_enabled) to the whole component tree. Components that render geo
// charts or tabs read from here so they react instantly when an admin
// toggles a flag on the Settings page.

interface GeoSettingsState {
  settings: GeoSettings;
  loading: boolean;
  /** Re-fetch from the server — call after a successful PUT /settings/geo. */
  reload: () => Promise<void>;
}

/** Safe defaults — both features on until the server responds. */
const DEFAULT: GeoSettings = { ixp_enabled: true, nix_enabled: true };

const Ctx = createContext<GeoSettingsState | null>(null);

export function GeoSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<GeoSettings>(DEFAULT);
  const [loading, setLoading] = useState(true);

  const fetch_ = useCallback(async () => {
    try {
      const s = await api.geoSettings();
      setSettings(s);
    } catch {
      // If the server doesn't have the endpoint yet (old install), keep defaults.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch_();
  }, [fetch_]);

  return (
    <Ctx.Provider value={{ settings, loading, reload: fetch_ }}>
      {children}
    </Ctx.Provider>
  );
}

export function useGeoSettings(): GeoSettingsState {
  const c = useContext(Ctx);
  if (!c) throw new Error("useGeoSettings must be used inside GeoSettingsProvider");
  return c;
}
